import { useMemo } from "react";
import { type LandingPageConfig, type PlaceFeature, type PlaceType } from "~/lib/api";
import { getAccentForType } from "~/lib/placeAccents";
import { sortPlaceTypes } from "~/lib/placeTypes";
import { PlaceSection } from "./PlaceSection";

interface GroupedPlaceCardsProps {
  places: PlaceFeature[];
  selectedId: string | null;
  sectionConfigs?: LandingPageConfig["sections"];
  emptyState?: React.ReactNode;
}

export function GroupedPlaceCards({ places, selectedId, sectionConfigs, emptyState }: GroupedPlaceCardsProps) {
  const { placesByType, typeOrder } = useMemo(() => {
    const map = new Map<PlaceType, PlaceFeature[]>();
    for (const place of places) {
      const type = place.properties.type;
      const list = map.get(type) ?? [];
      list.push(place);
      map.set(type, list);
    }
    return { placesByType: map, typeOrder: sortPlaceTypes(Array.from(map.keys())) };
  }, [places]);

  if (places.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <div className="@container p-4 space-y-12">
      {typeOrder.map((type) => (
        <PlaceSection
          key={type}
          type={type}
          places={placesByType.get(type)!}
          selectedId={selectedId}
          accent={getAccentForType(type, typeOrder)}
          description={sectionConfigs?.[type]?.description}
        />
      ))}
    </div>
  );
}
